import React, { Component } from 'react';
import { toast } from 'react-toastify';
import { SessionContext } from './SessionContext.js';
import { Section } from '../Common.js';
import NslMailAPI from '../NslMailAPI.js';

class SignupForm extends Component {
    static contextType = SessionContext;

    constructor(props) {
        super(props);
        this.state = {
            first_name: '',
            last_name: '',
            username: '',
            primary_email: '',
            password: '',
            password2: '',
            reason: '',
            website: '',   // honeypot -- see the hidden field in render()
            isSubmitting: false,
            submitted: false,
            powMessage: '',
            errors: {}
        };
    }

    handleChange = (e) => {
        const { name, value } = e.target;
        this.setState({ 
            [name]: value,
            errors: { ...this.state.errors, [name]: '' }
        });
    }

    validateForm = () => {
        const errors = {};
        const { first_name, last_name, username, primary_email, password, password2, reason } = this.state;

        if (!first_name.trim()) errors.first_name = 'First name is required';
        if (!last_name.trim()) errors.last_name = 'Last name is required';
        if (!username.trim()) errors.username = 'Username is required';
        if (!primary_email.trim()) errors.primary_email = 'Email is required';
        if (!password) errors.password = 'Password is required';
        if (!password2) errors.password2 = 'Please confirm your password';

        // Matches the server-side bound in POST /signup.
        if (!reason.trim()) {
            errors.reason = 'Please tell us why you want an account';
        } else if (reason.trim().length < 20) {
            errors.reason = 'Please tell us a little more (at least 20 characters)';
        } else if (reason.trim().length > 2000) {
            errors.reason = 'Please keep this under 2000 characters';
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (primary_email && !emailRegex.test(primary_email)) {
            errors.primary_email = 'Please enter a valid email address';
        }

        // Username validation
        if (username && username.length < 3) {
            errors.username = 'Username must be at least 3 characters long';
        }

        // Password validation
        if (password && password.length < 6) {
            errors.password = 'Password must be at least 6 characters long';
        }

        if (password !== password2) {
            errors.password2 = 'Passwords do not match';
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    }

    handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!this.validateForm()) {
            return;
        }

        this.setState({ isSubmitting: true });

        try {
            const API = new NslMailAPI();
            await API.signup({
                first_name: this.state.first_name,
                last_name: this.state.last_name,
                username: this.state.username,
                primary_email: this.state.primary_email,
                password: this.state.password,
                password2: this.state.password2,
                reason: this.state.reason,
                website: this.state.website
            }, (powMessage) => this.setState({ powMessage }));

            // Signup no longer logs you in -- the account is not usable until an
            // admin approves it, so there is no token to store and nowhere to go.
            this.setState({ submitted: true });
        } catch (error) {
            console.error('Signup error:', error);
            toast.error(error.message || 'Error creating account. Please try again.');
        } finally {
            this.setState({ isSubmitting: false, powMessage: '' });
        }
    }

    render() {
        const { first_name, last_name, username, primary_email, password, password2, reason, website, isSubmitting, submitted, powMessage, errors } = this.state;

        if (submitted) {
            return (
                <Section name="Request Received">
                    <div className="container-md">
                        <div className="card">
                            <div className="card-header">
                                <h2 className="text-center text-xl font-semibold text-primary-600" style={{margin: 0}}>
                                    Thanks &mdash; your request is under review
                                </h2>
                            </div>
                            <div className="card-content">
                                <p className="text-gray-600">
                                    NSL Mail accounts are approved by hand, so there are a couple of
                                    steps left:
                                </p>
                                <ol className="text-gray-600" style={{paddingLeft: "var(--space-6)"}}>
                                    <li>
                                        We've emailed <strong>{primary_email}</strong> a verification
                                        link. Please click it to confirm the address is yours.
                                    </li>
                                    <li>
                                        We'll review your request and email you as soon as your
                                        account is active. You won't be able to sign in until then.
                                    </li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </Section>
            );
        }

        return (
            <Section name="Create Your NSL Mail Account">
                <div className="container-md">
                    <div className="card">
                        <div className="card-header">
                            <h2 className="text-center text-xl font-semibold text-primary-600" style={{margin: 0}}>
                                Join NSL Mail
                            </h2>
                            <p className="text-center text-gray-600 mt-2" style={{margin: "var(--space-2) 0 0 0"}}>
                                Protect your email privacy with secure aliases
                            </p>
                        </div>
                        <div className="card-content">
                            <form onSubmit={this.handleSubmit}>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="form-group">
                                        <label className="form-label">First Name</label>
                                        <input 
                                            type="text"
                                            name="first_name"
                                            value={first_name}
                                            onChange={this.handleChange}
                                            disabled={isSubmitting}
                                            className={`form-input ${errors.first_name ? 'error' : ''}`}
                                            placeholder="Enter your first name"
                                        />
                                        {errors.first_name && <div className="form-error">{errors.first_name}</div>}
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Last Name</label>
                                        <input 
                                            type="text"
                                            name="last_name"
                                            value={last_name}
                                            onChange={this.handleChange}
                                            disabled={isSubmitting}
                                            className={`form-input ${errors.last_name ? 'error' : ''}`}
                                            placeholder="Enter your last name"
                                        />
                                        {errors.last_name && <div className="form-error">{errors.last_name}</div>}
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="form-label">Username</label>
                                    <input 
                                        type="text"
                                        name="username"
                                        value={username}
                                        onChange={this.handleChange}
                                        disabled={isSubmitting}
                                        className={`form-input ${errors.username ? 'error' : ''}`}
                                        placeholder="Choose a unique username"
                                    />
                                    {errors.username && <div className="form-error">{errors.username}</div>}
                                    <div className="form-help">This will be your login username</div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="form-label">Email Address</label>
                                    <input 
                                        type="email"
                                        name="primary_email"
                                        value={primary_email}
                                        onChange={this.handleChange}
                                        disabled={isSubmitting}
                                        className={`form-input ${errors.primary_email ? 'error' : ''}`}
                                        placeholder="your.email@example.com"
                                    />
                                    {errors.primary_email && <div className="form-error">{errors.primary_email}</div>}
                                    <div className="form-help">Your real email address (kept private)</div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Why do you want an account?</label>
                                    <textarea
                                        name="reason"
                                        value={reason}
                                        onChange={this.handleChange}
                                        disabled={isSubmitting}
                                        rows="4"
                                        className={`form-input ${errors.reason ? 'error' : ''}`}
                                        style={{ resize: 'vertical', minHeight: '90px' }}
                                        placeholder="Tell us a little about what you'll use NSL Mail for."
                                    />
                                    {errors.reason && <div className="form-error">{errors.reason}</div>}
                                    <div className="form-help">
                                        New accounts are reviewed by a person before they're activated.
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="form-group">
                                        <label className="form-label">Password</label>
                                        <input 
                                            type="password"
                                            name="password"
                                            value={password}
                                            onChange={this.handleChange}
                                            disabled={isSubmitting}
                                            className={`form-input ${errors.password ? 'error' : ''}`}
                                            placeholder="Create a secure password"
                                        />
                                        {errors.password && <div className="form-error">{errors.password}</div>}
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Confirm Password</label>
                                        <input 
                                            type="password"
                                            name="password2"
                                            value={password2}
                                            onChange={this.handleChange}
                                            disabled={isSubmitting}
                                            className={`form-input ${errors.password2 ? 'error' : ''}`}
                                            placeholder="Confirm your password"
                                        />
                                        {errors.password2 && <div className="form-error">{errors.password2}</div>}
                                    </div>
                                </div>
                                
                                {/* Honeypot. Hidden from people, irresistible to
                                    form-filling bots; the server silently discards
                                    anything that fills it. */}
                                <div className="form-trap" aria-hidden="true">
                                    <label htmlFor="website">Website</label>
                                    <input
                                        type="text"
                                        id="website"
                                        name="website"
                                        value={website}
                                        onChange={this.handleChange}
                                        tabIndex="-1"
                                        autoComplete="off"
                                        data-lpignore="true"
                                        data-1p-ignore=""
                                    />
                                </div>

                                <div className="text-center mt-8">
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-lg"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Sending Request...' : 'Request an Account'}
                                    </button>
                                    {powMessage && (
                                        <div className="form-help">{powMessage}</div>
                                    )}
                                </div>
                            </form>
                        </div>
                        <div className="card-footer text-center">
                            <p className="text-sm text-gray-600" style={{margin: "0 0 var(--space-2) 0"}}>
                                Already have an account? <a href="/" className="text-primary-600">Sign in here</a>
                            </p>
                            <p className="text-sm text-gray-500" style={{margin: 0}}>
                                By creating an account, you agree to our <a href="/privacy" className="text-primary-600">Privacy Policy</a>
                            </p>
                        </div>
                    </div>
                </div>
            </Section>
        );
    }
}

export default SignupForm;